// @ts-nocheck
// app/admin/export.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Platform,
  Alert,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Download, ArrowLeft, FileText, Table, ShieldAlert } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';

/**
 * ✅ FIXED (CRIT-1): Admin export now has a real auth guard (same logic as dashboard)
 * - Blocks /admin/export for non-admin users
 * - Kicks out on session expiry (onAuthStateChange)
 *
 * Notes:
 * - This keeps the export simple and predictable.
 * - It still fetches up to LIMIT rows per table (not streaming).
 */

type ExportTableKey = 'leads' | 'chat_sessions' | 'course_selections' | 'reviews';
type ExportFormat = 'json' | 'csv';

const LIMIT = 10_000;

const TABLES: { key: ExportTableKey; label: string; icon: any }[] = [
  { key: 'leads', label: 'Leads', icon: FileText },
  { key: 'chat_sessions', label: 'Chat Sessions', icon: Table },
  { key: 'course_selections', label: 'Course Selections', icon: Table },
  { key: 'reviews', label: 'Reviews', icon: FileText },
];

function nowStamp() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(
    d.getMinutes()
  )}-${pad(d.getSeconds())}`;
}

function toCSV(rows: any[]) {
  if (!Array.isArray(rows) || rows.length === 0) return '""\n';

  // union of keys (stable-ish)
  const keys = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r || {}).forEach((k) => set.add(k));
      return set;
    }, new Set<string>())
  );

  const esc = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    const safe = s.replace(/"/g, '""');
    return `"${safe}"`;
  };

  const header = keys.map(esc).join(',');
  const body = rows
    .map((r) => keys.map((k) => esc((r || {})[k])).join(','))
    .join('\n');

  return `${header}\n${body}\n`;
}

async function downloadFile(filename: string, content: string, mime: string) {
  if (Platform.OS === 'web') {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return;
  }

  // Native fallback (no filesystem dependency): show a success message.
  // If you want real native saving/sharing, we can add expo-file-system + expo-sharing later.
  Alert.alert('מוכן', `הקובץ "${filename}" מוכן להורדה. (במובייל צריך מנגנון שמירה/שיתוף כדי להוציא קובץ בפועל)`);
}

export default function AdminExport() {
  const router = useRouter();

  const [authChecking, setAuthChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('json');

  const [selected, setSelected] = useState<Record<ExportTableKey, boolean>>({
    leads: true,
    chat_sessions: true,
    course_selections: true,
    reviews: true,
  });

  const [progress, setProgress] = useState<{
    phase: 'idle' | 'fetching' | 'building' | 'downloading' | 'done' | 'error';
    message: string;
  }>({ phase: 'idle', message: '' });

  const runIdRef = useRef(0);

  // ✅ Same guard logic as dashboard
  const guardAdmin = useCallback(async (): Promise<boolean> => {
    try {
      setAuthChecking(true);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        router.replace('/admin/login');
        return false;
      }

      const { data: adminRow, error: adminErr } = await supabase
        .from('admins')
        .select('id, is_active')
        .eq('user_id', session.user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (adminErr || !adminRow) {
        await supabase.auth.signOut();
        router.replace('/admin/login');
        return false;
      }

      return true;
    } catch {
      try {
        await supabase.auth.signOut();
      } catch {}
      router.replace('/admin/login');
      return false;
    } finally {
      setAuthChecking(false);
    }
  }, [router]);

  // ✅ Initial guard
  useEffect(() => {
    const runId = ++runIdRef.current;

    (async () => {
      const ok = await guardAdmin();
      if (!ok) return;
      if (runId !== runIdRef.current) return;
    })();

    return () => {
      runIdRef.current++;
    };
  }, [guardAdmin]);

  // ✅ Kick out if auth changes / session expires
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user?.id) {
        router.replace('/admin/login');
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [router]);

  const selectedKeys = useMemo(
    () => (Object.keys(selected) as ExportTableKey[]).filter((k) => selected[k]),
    [selected]
  );

  const toggleTable = (k: ExportTableKey) => {
    setSelected((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  const exportData = useCallback(async () => {
    const runId = ++runIdRef.current;

    try {
      setLoading(true);
      setProgress({ phase: 'fetching', message: 'מאמת הרשאות...' });

      const ok = await guardAdmin();
      if (!ok) return;
      if (runId !== runIdRef.current) return;

      if (selectedKeys.length === 0) {
        Alert.alert('שגיאה', 'בחר לפחות טבלה אחת לייצוא');
        return;
      }

      setProgress({ phase: 'fetching', message: 'טוען נתונים מהשרת...' });

      const payload: Record<string, any[]> = {};

      // fetch sequentially (more predictable + easier progress)
      for (let i = 0; i < selectedKeys.length; i++) {
        const table = selectedKeys[i];
        setProgress({
          phase: 'fetching',
          message: `טוען ${table} (${i + 1}/${selectedKeys.length})...`,
        });

        const { data, error } = await supabase.from(table).select('*').limit(LIMIT);

        if (runId !== runIdRef.current) return;

        if (error) {
          console.error(`[Export] ${table} error:`, error);
          throw new Error(`שגיאה בטעינת ${table}`);
        }

        payload[table] = (data as any[]) || [];
      }

      setProgress({ phase: 'building', message: 'מכין קובץ...' });

      const stamp = nowStamp();
      const baseName = `kursi_export_${stamp}`;

      if (format === 'json') {
        const content = JSON.stringify(
          {
            exported_at: new Date().toISOString(),
            limit: LIMIT,
            tables: selectedKeys,
            data: payload,
          },
          null,
          2
        );

        setProgress({ phase: 'downloading', message: 'מוריד קובץ...' });
        await downloadFile(`${baseName}.json`, content, 'application/json; charset=utf-8');
      } else {
        // CSV: one CSV per table (clean + avoids mixing schemas)
        for (let i = 0; i < selectedKeys.length; i++) {
          const table = selectedKeys[i];
          setProgress({
            phase: 'downloading',
            message: `מוריד CSV של ${table} (${i + 1}/${selectedKeys.length})...`,
          });

          const csv = toCSV(payload[table] || []);
          await downloadFile(`${baseName}__${table}.csv`, csv, 'text/csv; charset=utf-8');

          if (runId !== runIdRef.current) return;
        }
      }

      setProgress({ phase: 'done', message: 'הייצוא הושלם' });
    } catch (e: any) {
      console.error('Export error:', e);
      setProgress({ phase: 'error', message: 'הייצוא נכשל' });
      Alert.alert('שגיאה', e?.message || 'לא ניתן לייצא נתונים');
    } finally {
      setLoading(false);
    }
  }, [format, guardAdmin, selectedKeys]);

  if (authChecking) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>מאמת הרשאות...</Text>
      </View>
    );
  }

  return (
    <View style={styles.page}>
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.7 }]}>
          <ArrowLeft size={18} color={COLORS.text.primary} strokeWidth={2} />
        </Pressable>

        <View style={{ flex: 1 }}>
          <Text style={styles.title}>ייצוא נתונים</Text>
          <Text style={styles.subtitle}>גישה לאדמינים בלבד · עד {LIMIT.toLocaleString('he-IL')} רשומות לכל טבלה</Text>
        </View>

        <View style={styles.badge}>
          <ShieldAlert size={14} color={COLORS.primary} strokeWidth={2} />
          <Text style={styles.badgeText}>Admin only</Text>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>מה לייצא</Text>

          <View style={styles.grid}>
            {TABLES.map(({ key, label, icon: Icon }) => {
              const active = !!selected[key];
              return (
                <Pressable
                  key={key}
                  onPress={() => toggleTable(key)}
                  style={({ pressed }) => [
                    styles.tile,
                    active && styles.tileActive,
                    pressed && { opacity: 0.85 },
                  ]}
                >
                  <View style={[styles.tileIcon, active && { backgroundColor: COLORS.primaryBg }]}>
                    <Icon size={18} color={active ? COLORS.primary : COLORS.text.secondary} strokeWidth={2} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.tileTitle, active && { color: COLORS.text.primary }]}>{label}</Text>
                    <Text style={styles.tileSub}>{key}</Text>
                  </View>
                  <View style={[styles.check, active && styles.checkActive]} />
                </Pressable>
              );
            })}
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>פורמט</Text>
          <View style={styles.row}>
            <Pressable
              onPress={() => setFormat('json')}
              style={({ pressed }) => [styles.chip, format === 'json' && styles.chipActive, pressed && { opacity: 0.8 }]}
            >
              <Text style={[styles.chipText, format === 'json' && styles.chipTextActive]}>JSON (קובץ אחד)</Text>
            </Pressable>

            <Pressable
              onPress={() => setFormat('csv')}
              style={({ pressed }) => [styles.chip, format === 'csv' && styles.chipActive, pressed && { opacity: 0.8 }]}
            >
              <Text style={[styles.chipText, format === 'csv' && styles.chipTextActive]}>CSV (קובץ לכל טבלה)</Text>
            </Pressable>
          </View>

          <View style={styles.note}>
            <Text style={styles.noteText}>
              {format === 'json'
                ? 'JSON כולל את כל הטבלאות שבחרת בתוך קובץ אחד.'
                : 'CSV יורד כקובץ נפרד לכל טבלה (יותר נקי כשיש סכמות שונות).'}
            </Text>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>סטטוס</Text>
          <View style={styles.statusRow}>
            {loading ? <ActivityIndicator color={COLORS.primary} /> : <View style={styles.dot} />}
            <Text style={styles.statusText}>
              {progress.phase === 'idle' ? 'מוכן לייצוא' : progress.message || '—'}
            </Text>
          </View>

          <Pressable
            onPress={exportData}
            disabled={loading}
            style={({ pressed }) => [
              styles.exportBtn,
              loading && { opacity: 0.6 },
              pressed && !loading && { opacity: 0.9 },
            ]}
          >
            <Download size={18} color="#FFF" strokeWidth={2.5} />
            <Text style={styles.exportBtnText}>{loading ? 'מייצא...' : 'ייצוא והורדה'}</Text>
          </Pressable>
        </View>

        <View style={styles.footerSpace} />
      </ScrollView>
    </View>
  );
}

const COLORS = {
  bg: '#F6F7FB',
  surface: '#FAF5EA',
  border: '#E6E9EF',
  borderLight: '#F5F6F8',
  shadow: 'rgba(0, 0, 0, 0.08)',
  primary: '#0073EA',
  primaryBg: '#E5F4FF',
  text: {
    primary: '#323338',
    secondary: '#676879',
    tertiary: '#9699A6',
  },
  error: '#E44258',
};

const SHADOW = Platform.select({
  ios: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
  },
  android: {
    elevation: 3,
  },
  web: {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
}) as any;

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: COLORS.bg },

  loading: {
    flex: 1,
    backgroundColor: COLORS.bg,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  loadingText: { color: COLORS.text.secondary, fontSize: 14 },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 14,
    backgroundColor: COLORS.surface,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    ...SHADOW,
  },
  iconBtn: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: COLORS.bg,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  title: { fontSize: 18, fontWeight: '700', color: COLORS.text.primary },
  subtitle: { marginTop: 2, fontSize: 12, color: COLORS.text.secondary },

  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.primaryBg,
  },
  badgeText: { fontSize: 12, color: COLORS.primary, fontWeight: '700' },

  content: {
    padding: 20,
    gap: 14,
    width: '100%',
    maxWidth: 1100,
    marginHorizontal: 'auto',
  },

  card: {
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 0,
    padding: 16,
    gap: 12,
    ...SHADOW,
  },

  sectionTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text.primary },

  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  tile: {
    flexGrow: 1,
    minWidth: Platform.OS === 'web' ? 240 : '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.bg,
    borderRadius: 0,
  },
  tileActive: {
    borderColor: COLORS.primary,
    backgroundColor: '#F0F7FF',
  },
  tileIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: COLORS.borderLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileTitle: { fontSize: 13, fontWeight: '700', color: COLORS.text.secondary },
  tileSub: { marginTop: 2, fontSize: 12, color: COLORS.text.tertiary },
  check: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: COLORS.border,
    backgroundColor: '#FFF',
  },
  checkActive: {
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primary,
  },

  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.bg,
  },
  chipActive: {
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primary,
  },
  chipText: { fontSize: 13, color: COLORS.text.secondary, fontWeight: '600' },
  chipTextActive: { color: '#FFF' },

  note: {
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: '#FAFBFC',
    padding: 12,
    borderRadius: 12,
  },
  noteText: { fontSize: 12, color: COLORS.text.secondary, lineHeight: 18 },

  statusRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  dot: { width: 10, height: 10, borderRadius: 5, backgroundColor: '#00C875' },
  statusText: { fontSize: 13, color: COLORS.text.secondary, fontWeight: '600' },

  exportBtn: {
    marginTop: 6,
    backgroundColor: COLORS.primary,
    borderRadius: 0,
    paddingVertical: 14,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  exportBtnText: { color: '#FFF', fontSize: 14, fontWeight: '800' },

  footerSpace: { height: 24 },
});